/**
 * AdminCataloguePage.js — /admin/catalogue
 *
 * RC 1.26.A.1 — Admin-only operational view of the employer catalogue.
 *
 * Reads `GET /api/admin/catalogue` (the structured JSON sibling of the
 * canonical markdown catalogue) and renders a sortable, filterable
 * table of 108 global employers. Per-row actions:
 *
 *   - status_bucket === "seedable_now"        → "Seed Source" button
 *       → POST /api/admin/catalogue/seed
 *       → inline result pill: created / already_exists / error
 *
 *   - status_bucket === "pending_family_adapter" | "bespoke_required"
 *       → "View Why" opens a dialog with the detection notes, the
 *         unlock slice (e.g. "RC 1.26.B.1"), and the raw family.
 *
 * Search / filters are client-side (the payload is ~70 kB). No
 * polling, no auto-refresh, no bulk actions. A manual "Refresh"
 * button re-fetches the JSON.
 *
 * Access model mirrors `/admin/sources`: the route is mounted inside
 * `ProtectedRoute requireProfile={true}` and enforcement lives
 * server-side — non-admin users get a 403 from the API, which we
 * render as an honest "Admin only" placeholder.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { buildCoverageMap } from '@/lib/catalogueCoverage';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2, RefreshCw, ShieldAlert, CheckCircle2, AlertCircle,
  Info, Search, Globe2, Sparkles, Activity, CircleDot,
} from 'lucide-react';

const BUCKET_META = {
  seedable_now:            { label: 'Seedable now',   tone: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40' },
  pending_family_adapter:  { label: 'Pending family', tone: 'bg-amber-500/10 text-amber-200 border-amber-500/40' },
  bespoke_required:        { label: 'Bespoke',        tone: 'bg-ndor-surface-alt text-slate-300 border-slate-200' },
};

const CONFIDENCE_TONE = {
  high:   'bg-emerald-500/10 text-emerald-200 border-emerald-500/40',
  medium: 'bg-sky-500/10 text-sky-200 border-sky-500/40',
  low:    'bg-amber-500/10 text-amber-200 border-amber-500/40',
  unknown:'bg-ndor-surface-alt text-slate-300 border-slate-200',
};

function humaniseFamily(family) {
  if (!family) return '—';
  return family.replace(/_pending$/, '').replace(/_board$/, '').replace(/_/g, ' ');
}

/**
 * RC 1.26.B.2.1 — Coverage cell. Shows whether a catalogue row is
 * already backed by an active `job_sources` row, and with how many
 * currently active jobs. Rendered in the new Coverage column.
 *
 * Honesty rules:
 *   - pending/bespoke rows → nothing to seed against → render em dash.
 *   - unmatched seedable rows → muted "Not seeded" so the operator
 *     is explicitly told the row has no active source.
 *   - matched rows with active_jobs > 0 → emerald "Ingesting N".
 *   - matched rows with active_jobs === 0 → slate "Seeded · empty".
 *   - matched but disabled → amber "Seeded · disabled".
 *   - `active_jobs === null` (freshness call failed) → "Seeded".
 *   - A tiny "fallback" caption is shown whenever the match was via
 *     name+family rather than an exact URL, so the operator knows
 *     the verdict is inferred, not certain.
 */
function CoverageCell({ row, coverage }) {
  if (row.status_bucket !== 'seedable_now') {
    return <span className="text-slate-300 text-xs">—</span>;
  }
  const slug = row.company_name.toLowerCase().replace(/\s+/g, '-');
  if (!coverage?.matched) {
    return (
      <span
        data-testid={`admin-catalogue-coverage-not-seeded-${slug}`}
        className="text-xs text-slate-300"
      >
        Not seeded
      </span>
    );
  }
  const { active_jobs, enabled, match_type, source_name } = coverage;
  // Render a compact inline badge.
  let tone = 'bg-emerald-500/10 text-emerald-200 border-emerald-500/40';
  let icon = <Activity className="h-3 w-3" />;
  let label;
  if (enabled === false) {
    tone = 'bg-amber-500/10 text-amber-200 border-amber-500/40';
    icon = <CircleDot className="h-3 w-3" />;
    label = 'Seeded · disabled';
  } else if (typeof active_jobs === 'number' && active_jobs > 0) {
    label = `Ingesting ${active_jobs}`;
  } else if (active_jobs === 0) {
    tone = 'bg-ndor-surface-alt text-slate-300 border-slate-200';
    icon = <CircleDot className="h-3 w-3" />;
    label = 'Seeded · empty';
  } else {
    label = 'Seeded';
  }
  const fallback = match_type === 'name_family';
  return (
    <div
      className="flex flex-col gap-0.5"
      data-testid={`admin-catalogue-coverage-${slug}`}
    >
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs w-fit ${tone}`}
        title={source_name || ''}
      >
        {icon}
        <span data-testid={`admin-catalogue-coverage-${slug}-label`}>{label}</span>
      </span>
      {fallback && (
        <span
          className="text-[10px] text-slate-300"
          data-testid={`admin-catalogue-coverage-${slug}-fallback`}
          title="Matched by source_type + name — not by exact URL."
        >
          name match
        </span>
      )}
    </div>
  );
}

export default function AdminCataloguePage() {
  const [data, setData] = useState(null);          // {generated_at,total,items}
  const [sources, setSources] = useState([]);      // job_sources rows
  const [freshness, setFreshness] = useState([]);  // freshness rows (active_jobs)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  // Filters / search
  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [familyFilter, setFamilyFilter] = useState('all');

  // Per-row seed action state. Keyed by careers_url.
  const [seedState, setSeedState] = useState({});
  const [whyRow, setWhyRow] = useState(null);

  // Regenerate action state. One of: null | 'running' | 'ok' | 'error'.
  const [regenState, setRegenState] = useState(null);
  const [regenMsg, setRegenMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setForbidden(false);
    try {
      // Parallel fetch: catalogue + already-seeded job_sources (for the
      // `_detected_from_url` + name join) + freshness (for the active
      // jobs count). Freshness is best-effort — if it fails we still
      // render coverage using the sources list alone.
      const [d, srcResp, freshResp] = await Promise.all([
        apiFetch('/api/admin/catalogue'),
        apiFetch('/api/admin/sources').catch((e) => {
          // If sources fails, coverage is simply not shown — the seed
          // UI itself does not depend on it.
          if (e?.status === 403) throw e;           // propagate admin-only failure
          return { items: [] };
        }),
        apiFetch('/api/admin/sources/freshness').catch(() => ({ items: [] })),
      ]);
      setData(d);
      setSources(srcResp?.items || []);
      setFreshness(freshResp?.items || []);
    } catch (err) {
      if (err?.status === 403) setForbidden(true);
      else setError(err?.message || 'Failed to load catalogue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allFamilies = useMemo(() => {
    if (!data?.items) return [];
    const set = new Set();
    data.items.forEach((i) => i.detected_family && set.add(i.detected_family));
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    const q = search.trim().toLowerCase();
    return data.items.filter((i) => {
      if (bucketFilter !== 'all' && i.status_bucket !== bucketFilter) return false;
      if (familyFilter !== 'all' && (i.detected_family || 'none') !== familyFilter) return false;
      if (q && !i.company_name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, search, bucketFilter, familyFilter]);

  const counts = useMemo(() => {
    const c = { total: 0, seedable_now: 0, pending_family_adapter: 0, bespoke_required: 0 };
    if (data?.items) {
      c.total = data.items.length;
      data.items.forEach((i) => {
        c[i.status_bucket] = (c[i.status_bucket] || 0) + 1;
      });
    }
    return c;
  }, [data]);

  // RC 1.26.B.2.1 — coverage map keyed by careers_url. Joins
  // job_sources + freshness onto catalogue rows so the table can
  // distinguish seeded vs non-seeded employers at a glance.
  const coverageMap = useMemo(
    () => buildCoverageMap(data?.items || [], sources, freshness),
    [data, sources, freshness],
  );

  const coverageCounts = useMemo(() => {
    const out = { seeded: 0, ingesting: 0, seeded_empty: 0, not_seeded: 0 };
    if (!data?.items) return out;
    for (const row of data.items) {
      if (row.status_bucket !== 'seedable_now') continue;
      const cov = coverageMap[row.careers_url];
      if (cov?.matched) {
        out.seeded += 1;
        if ((cov.active_jobs || 0) > 0) out.ingesting += 1;
        else out.seeded_empty += 1;
      } else {
        out.not_seeded += 1;
      }
    }
    return out;
  }, [data, coverageMap]);

  async function regenerate() {
    setRegenState('running');
    setRegenMsg(null);
    try {
      const resp = await apiFetch('/api/admin/catalogue/regenerate', {
        method: 'POST',
      });
      setRegenState('ok');
      setRegenMsg(
        `Regenerated ${resp.total} employers (${resp.seedable_now} seedable)`
      );
      // Re-fetch the catalogue so the UI reflects the new artifact.
      await load();
      // Wipe per-row seed pills — state may be stale vs the new payload.
      setSeedState({});
    } catch (err) {
      setRegenState('error');
      const detail = err?.data?.detail;
      setRegenMsg(
        (typeof detail === 'string' && detail)
        || err?.message
        || 'Regeneration failed.'
      );
    }
  }

  async function seedRow(row) {
    const key = row.careers_url;
    setSeedState((s) => ({ ...s, [key]: { status: 'pending' } }));
    try {
      const resp = await apiFetch('/api/admin/catalogue/seed', {
        method: 'POST',
        body: { company_name: row.company_name, careers_url: row.careers_url },
      });
      setSeedState((s) => ({ ...s, [key]: { status: resp.status, source_id: resp.source_id } }));
      // Refresh the sources list so the new coverage badge appears
      // without requiring a full page refresh. Freshness may lag a
      // beat (the worker hasn't run yet) — that's fine; the badge
      // will read "Seeded" until the first successful ingestion.
      if (resp.status === 'created' || resp.status === 'already_exists') {
        try {
          const [srcResp, freshResp] = await Promise.all([
            apiFetch('/api/admin/sources'),
            apiFetch('/api/admin/sources/freshness'),
          ]);
          setSources(srcResp?.items || []);
          setFreshness(freshResp?.items || []);
        } catch { /* best-effort */ }
      }
    } catch (err) {
      const detail = err?.data?.detail;
      const msg = (typeof detail === 'string' && detail)
        || (detail && detail.message)
        || err?.message
        || 'Seed failed.';
      setSeedState((s) => ({
        ...s, [key]: { status: 'error', message: msg },
      }));
    }
  }

  // ────────────────────────────────────────────────────────────
  // Rendering
  // ────────────────────────────────────────────────────────────

  if (forbidden) {
    return (
      <div
        data-testid="admin-catalogue-forbidden"
        className="mx-auto max-w-xl py-16 px-4 text-center"
      >
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-xl font-semibold text-ndor-ink">Admin only</h1>
        <p className="mt-2 text-sm text-slate-300">
          You need operator access to view the employer catalogue.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-catalogue-page"
      className="mx-auto max-w-[1500px] px-4 py-8 space-y-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Globe2 className="h-6 w-6 text-slate-200" />
            <h1 className="text-2xl font-semibold text-ndor-ink">Employer catalogue</h1>
          </div>
          <p className="mt-1 text-sm text-slate-300 max-w-2xl">
            Global roster of {counts.total} employers, classified by detected ATS family.
            Seed the ones on supported adapters today; the rest are flagged with the
            future RC slice that will unlock them.
          </p>
          {data?.generated_at && (
            <p className="mt-1 text-xs text-slate-300" data-testid="admin-catalogue-generated-at">
              Catalogue generated {new Date(data.generated_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-catalogue-regenerate-btn"
            variant="default"
            onClick={regenerate}
            disabled={regenState === 'running' || loading}
            title="Re-run the 108-employer probe and rewrite the catalogue artifacts."
          >
            {regenState === 'running'
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Sparkles className="mr-2 h-4 w-4" />}
            {regenState === 'running' ? 'Regenerating…' : 'Regenerate catalogue'}
          </Button>
          <Button
            data-testid="admin-catalogue-refresh-btn"
            variant="outline"
            onClick={load}
            disabled={loading || regenState === 'running'}
          >
            {loading
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Regenerate result banner. Idle → nothing; success/error → inline. */}
      {regenState === 'ok' && regenMsg && (
        <div
          data-testid="admin-catalogue-regen-ok"
          className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200 flex items-center gap-2"
        >
          <CheckCircle2 className="h-4 w-4" /> {regenMsg}
        </div>
      )}
      {regenState === 'error' && regenMsg && (
        <div
          data-testid="admin-catalogue-regen-error"
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-sm text-rose-200 flex items-center gap-2"
        >
          <AlertCircle className="h-4 w-4" /> {regenMsg}
        </div>
      )}

      {/* Scorecard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: 'total', label: 'Total', value: counts.total, tone: 'bg-ndor-surface-alt text-slate-100' },
          { key: 'seedable_now', label: 'Seedable now', value: counts.seedable_now, tone: 'bg-emerald-500/10 text-emerald-200' },
          { key: 'pending_family_adapter', label: 'Pending family adapter', value: counts.pending_family_adapter, tone: 'bg-amber-500/10 text-amber-200' },
          { key: 'bespoke_required', label: 'Bespoke required', value: counts.bespoke_required, tone: 'bg-ndor-surface-alt text-slate-200' },
        ].map((c) => (
          <div
            key={c.key}
            className={`rounded-md border border-slate-200 px-4 py-3 ${c.tone}`}
            data-testid={`admin-catalogue-stat-${c.key}`}
          >
            <div className="text-xs uppercase tracking-wide opacity-70">{c.label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</div>
          </div>
        ))}
      </div>

      {/* RC 1.26.B.2.1 — coverage strip. Purely informational. */}
      {counts.seedable_now > 0 && (
        <div
          data-testid="admin-catalogue-coverage-summary"
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-300 border-l-2 border-emerald-300 pl-3"
        >
          <span className="font-medium text-slate-200">Coverage</span>
          <span className="inline-flex items-center gap-1 text-emerald-200">
            <Activity className="h-3.5 w-3.5" />
            <span data-testid="admin-catalogue-coverage-ingesting">
              {coverageCounts.ingesting}
            </span>{' '}ingesting
          </span>
          <span className="inline-flex items-center gap-1 text-slate-300">
            <CircleDot className="h-3.5 w-3.5" />
            <span data-testid="admin-catalogue-coverage-empty">
              {coverageCounts.seeded_empty}
            </span>{' '}seeded · empty
          </span>
          <span className="inline-flex items-center gap-1 text-slate-300">
            <span data-testid="admin-catalogue-coverage-not-seeded">
              {coverageCounts.not_seeded}
            </span>{' '}not seeded yet
          </span>
          <span className="text-slate-300">
            (out of {counts.seedable_now} seedable employers)
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
          <Input
            data-testid="admin-catalogue-search"
            placeholder="Search company name…"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={bucketFilter} onValueChange={setBucketFilter}>
          <SelectTrigger className="w-52" data-testid="admin-catalogue-bucket-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="seedable_now">Seedable now</SelectItem>
            <SelectItem value="pending_family_adapter">Pending family</SelectItem>
            <SelectItem value="bespoke_required">Bespoke</SelectItem>
          </SelectContent>
        </Select>
        <Select value={familyFilter} onValueChange={setFamilyFilter}>
          <SelectTrigger className="w-56" data-testid="admin-catalogue-family-filter">
            <SelectValue placeholder="All families" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All families</SelectItem>
            <SelectItem value="none">(none detected)</SelectItem>
            {allFamilies.map((f) => (
              <SelectItem key={f} value={f}>{humaniseFamily(f)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-slate-300 md:ml-auto" data-testid="admin-catalogue-match-count">
          {filtered.length} of {counts.total} matching
        </div>
      </div>

      {error && (
        <div
          data-testid="admin-catalogue-error"
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
        >
          {error}
        </div>
      )}

      <div className="rounded-md border border-slate-200 bg-ndor-surface overflow-x-auto">
        <Table data-testid="admin-catalogue-table">
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Careers URL</TableHead>
              <TableHead>Family</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Coverage</TableHead>
              <TableHead>Unlock</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-slate-300">
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading catalogue…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-slate-300">
                  No employers match these filters.
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((row) => {
              const meta = BUCKET_META[row.status_bucket] || BUCKET_META.bespoke_required;
              const state = seedState[row.careers_url];
              const coverage = coverageMap[row.careers_url] || { matched: false };
              return (
                <TableRow
                  key={row.careers_url}
                  data-testid={`admin-catalogue-row-${row.company_name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <TableCell>
                    <div className="font-medium text-ndor-ink">{row.company_name}</div>
                    <div className="text-xs text-slate-300">{row.category}</div>
                  </TableCell>
                  <TableCell className="max-w-[320px] text-xs">
                    <a
                      href={row.careers_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-200 hover:underline break-all"
                    >
                      {row.careers_url}
                    </a>
                  </TableCell>
                  <TableCell className="text-slate-200">{humaniseFamily(row.detected_family)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={CONFIDENCE_TONE[row.confidence] || CONFIDENCE_TONE.unknown}>
                      {row.confidence}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={meta.tone}>{meta.label}</Badge>
                  </TableCell>
                  <TableCell>
                    <CoverageCell
                      row={row}
                      coverage={coverage}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-slate-300">
                    {row.unlock_slice && row.unlock_slice !== '—'
                      ? row.unlock_slice
                      : <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {row.status_bucket === 'seedable_now' ? (
                      <div className="flex items-center justify-end gap-2">
                        {state?.status === 'created' && (
                          <span
                            data-testid="admin-catalogue-seed-created"
                            className="inline-flex items-center gap-1 text-xs text-emerald-200"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Created
                          </span>
                        )}
                        {state?.status === 'already_exists' && (
                          <span
                            data-testid="admin-catalogue-seed-exists"
                            className="inline-flex items-center gap-1 text-xs text-slate-300"
                          >
                            <Info className="h-3.5 w-3.5" /> Already exists
                          </span>
                        )}
                        {state?.status === 'error' && (
                          <span
                            data-testid="admin-catalogue-seed-error"
                            title={state.message}
                            className="inline-flex items-center gap-1 text-xs text-rose-200 max-w-[220px] truncate"
                          >
                            <AlertCircle className="h-3.5 w-3.5" /> {state.message}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant={state?.status === 'created' || state?.status === 'already_exists' ? 'outline' : 'default'}
                          onClick={() => seedRow(row)}
                          disabled={state?.status === 'pending'}
                          data-testid={`admin-catalogue-seed-btn-${row.company_name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {state?.status === 'pending'
                            ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                            : null}
                          {state?.status === 'created' || state?.status === 'already_exists'
                            ? 'Seed again'
                            : 'Seed Source'}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setWhyRow(row)}
                        data-testid={`admin-catalogue-why-btn-${row.company_name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        View Why
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!whyRow} onOpenChange={(o) => !o && setWhyRow(null)}>
        <DialogContent data-testid="admin-catalogue-why-dialog">
          {whyRow && (
            <>
              <DialogHeader>
                <DialogTitle>{whyRow.company_name} — why isn't this seedable?</DialogTitle>
                <DialogDescription>
                  Detection classified this employer as{' '}
                  <span className="font-medium text-slate-100">
                    {humaniseFamily(whyRow.detected_family)}
                  </span>
                  {' '}({whyRow.confidence}).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-300">Status</div>
                  <Badge
                    variant="outline"
                    className={(BUCKET_META[whyRow.status_bucket] || BUCKET_META.bespoke_required).tone}
                  >
                    {(BUCKET_META[whyRow.status_bucket] || BUCKET_META.bespoke_required).label}
                  </Badge>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-300">Unlock slice</div>
                  <div className="text-slate-200">
                    {whyRow.unlock_slice || 'custom per-company adapter required'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-300">Careers URL</div>
                  <a
                    href={whyRow.careers_url}
                    target="_blank" rel="noopener noreferrer"
                    className="text-sky-200 hover:underline text-xs break-all"
                  >
                    {whyRow.careers_url}
                  </a>
                </div>
                {whyRow.final_url && whyRow.final_url !== whyRow.careers_url && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-300">Final URL (after redirect)</div>
                    <div className="text-xs text-slate-300 break-all">{whyRow.final_url}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-300">Detection trail</div>
                  <ul className="mt-1 space-y-1 text-xs text-slate-300 list-disc list-inside">
                    {(whyRow.notes || []).map((n, i) => <li key={i}>{n}</li>)}
                    {(!whyRow.notes || !whyRow.notes.length) && <li className="list-none text-slate-300">No notes captured.</li>}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
